import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { apiErrorMessage } from "../../lib/api";
import DashboardLayout from "../../components/layout/DashboardLayout";
import StatusBadge from "../../components/StatusBadge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { formatCLP, formatUSD, formatDateTime } from "../../lib/format";

const STATUS_OPTIONS = [
    { value: "all", label: "All" },
    { value: "pending", label: "Pending" },
    { value: "paid", label: "Paid" },
    { value: "cancelled", label: "Cancelled" },
    { value: "refunded", label: "Refunded" },
];

export default function AdminPurchases() {
    const [purchases, setPurchases] = useState([]);
    const [filter, setFilter] = useState("all");

    const load = async () => {
        const params = filter === "all" ? {} : { status: filter };
        const { data } = await api.get("/admin/purchases", { params });
        setPurchases(data || []);
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

    const setStatus = async (p, status) => {
        try {
            await api.patch(`/admin/purchases/${p.id}/status`, { status });
            toast.success(`Purchase ${status === "paid" ? "marked as paid" : "updated"}`);
            await load();
        } catch (err) { toast.error(apiErrorMessage(err)); }
    };

    const remove = async (p) => {
        if (!window.confirm("Delete purchase?")) return;
        try {
            await api.delete(`/admin/purchases/${p.id}`);
            toast.success("Purchase deleted");            await load();
        } catch (err) { toast.error(apiErrorMessage(err)); }
    };

    const paypalRecapture = async (p) => {
        if (!window.confirm(
            `Retry the capture of this PayPal purchase (#${p.id.slice(0, 8)})?\n\n` +
            "Checks the actual order status with PayPal and captures the money if the buyer " +
            "approved it. Useful when the customer closed the browser before returning to the site."
        )) return;
        try {
            const { data } = await api.post(`/admin/purchases/${p.id}/paypal-recapture`);
            if (data.ok) {
                toast.success(
                    data.action === "captured" ? "✓ Payment captured — money sent to your PayPal"
                    : data.action === "finalized" ? "✓ Payment was already captured in PayPal — marked as paid"
                    : data.already === "paid" ? "It was already paid"
                    : `No action — PayPal reports status: ${data.paypal_status}`,
                    { description: `Order: ${p.provider_order_id || "—"}` }
                );
            } else {
                toast.error(data.detail || `PayPal status: ${data.paypal_status}`);
            }
            await load();
        } catch (err) { toast.error(apiErrorMessage(err)); }
    };

    return (
        <DashboardLayout role="admin">
            <div className="mb-8 flex items-center justify-between flex-wrap gap-4" data-testid="admin-purchases-page">
                <div>
                    <div className="label-mono mb-3">/ Purchase management</div>
                    <h1 className="font-heading font-black text-3xl lg:text-4xl tracking-tighter">Purchases</h1>
                </div>
                <Select value={filter} onValueChange={setFilter}>
                    <SelectTrigger className="bg-avx-surface border-avx-line h-11 w-48" data-testid="purchases-filter"><SelectValue /></SelectTrigger>
                    <SelectContent>
                        {STATUS_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                    </SelectContent>
                </Select>
            </div>

            <div className="panel overflow-x-auto">
                <table className="w-full text-sm min-w-[1000px]">
                    <thead className="bg-avx-surface/80 border-b border-avx-line">
                        <tr className="label-mono text-left">
                            <th className="px-4 py-3 font-normal">ID</th>
                            <th className="px-4 py-3 font-normal">Client</th>
                            <th className="px-4 py-3 font-normal">Product</th>
                            <th className="px-4 py-3 font-normal">Price</th>
                            <th className="px-4 py-3 font-normal">Method</th>
                            <th className="px-4 py-3 font-normal">Status</th>
                            <th className="px-4 py-3 font-normal">Date</th>
                            <th className="px-4 py-3 font-normal text-right">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {purchases.map((p) => (
                            <tr key={p.id} className="border-b border-avx-line/60 hover:bg-avx-surface/40" data-testid={`purchase-row-${p.id}`}>
                                <td className="px-4 py-3 font-mono text-xs">#{p.id.slice(0, 8)}</td>
                                <td className="px-4 py-3">
                                    <div>{p.user?.name}</div>
                                    <div className="font-mono text-[10px] text-avx-muted">{p.user?.email}</div>
                                </td>
                                <td className="px-4 py-3">{p.product?.name}</td>
                                <td className="px-4 py-3 font-mono">{p.currency === "USD" ? formatUSD(p.amount_paid) : formatCLP(p.amount_paid)}</td>
                                <td className="px-4 py-3 font-mono text-xs">{p.payment_method}</td>
                                <td className="px-4 py-3"><StatusBadge status={p.status} /></td>
                                <td className="px-4 py-3 font-mono text-xs">{formatDateTime(p.purchase_date || p.created_at)}</td>
                                <td className="px-4 py-3 text-right space-x-1">
                                    {p.status !== "paid" && p.payment_method === "paypal" && p.provider_order_id && (
                                        <button
                                            onClick={() => paypalRecapture(p)}
                                            className="text-xs font-mono uppercase text-avx-blue hover:underline"
                                            data-testid={`paypal-recapture-${p.id}`}
                                            title="Query PayPal and capture the payment if the buyer already approved"
                                        >
                                            Recapture PayPal
                                        </button>
                                    )}
                                    {p.status !== "paid" && (
                                        <button onClick={() => setStatus(p, "paid")} className="text-xs font-mono uppercase text-avx-success hover:underline" data-testid={`mark-paid-${p.id}`}>
                                            Paid
                                        </button>
                                    )}
                                    {p.status !== "cancelled" && (
                                        <button onClick={() => setStatus(p, "cancelled")} className="text-xs font-mono uppercase text-avx-muted hover:text-avx-danger">Cancel</button>
                                    )}
                                    <button onClick={() => remove(p)} className="text-xs font-mono uppercase text-avx-danger hover:underline">Delete</button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                {purchases.length === 0 && <div className="p-8 text-center text-avx-muted text-sm">No purchases.</div>}
            </div>
        </DashboardLayout>
    );
}
